import { ZoomableImage } from "@coworker/apprestructured/src/shared/components/ImageViewer/ZoomableImage/ZoomableImage";
import ClickableButtonWrapper from "@coworker/apprestructured/src/shared/wrappers/ClickableButtonWrapper/ClickableButtonWrapper";
import FixaCarousel, {
  FixaOverflowCarousel,
} from "@coworker/apprestructured/src/shared/wrappers/FixaCarousel/FixaCarousel";
import { useImageCarouselAccessibility } from "@coworker/apprestructured/src/shared/wrappers/FixaCarousel/useImageCarouselAccessibility";
import FixaImage from "@coworker/apprestructured/src/shared/wrappers/FixaImage/FixaImage";
import { FixaModal } from "@coworker/apprestructured/src/shared/wrappers/FixaModal/FixaModal";
import { FixaModalBody } from "@coworker/apprestructured/src/shared/wrappers/FixaModal/FixaModalBody";
import { FixaModalHeader } from "@coworker/apprestructured/src/shared/wrappers/FixaModal/FixaModalHeader";
import { FixaTheatre } from "@coworker/apprestructured/src/shared/wrappers/FixaModal/FixaTheatre";
import { FixaText } from "@coworker/apprestructured/src/shared/wrappers/FixaText/FixaText";
import LoadingCarousel from "@coworker/apprestructured/src/task-management/components/Images/LoadingCarousel/LoadingCarousel";
import { useFailedImageUrls } from "@coworker/apprestructured/src/task-management/hooks/useFailedImageUrls";
import {
  isLoading,
  Loading,
} from "@coworker/apprestructured/src/task-management/loading";
import { useTranslation } from "@coworker/locales";
import { useCallback, useMemo, useState } from "react";
import { useNotifications } from "../Notifications/NotificationsContext";
import styles from "./Images.module.css";

type ImagesProps = {
  urls: string[] | Loading;
};

export const Images = ({ urls }: ImagesProps) => {
  const [showModal, setShowModal] = useState(false);
  const [activeSlideImageIndex, setActiveSlideImageIndex] = useState(0);
  const { markImageAsFailed, isImageFailed } = useFailedImageUrls();
  const { showToast } = useNotifications();

  const { t } = useTranslation();
  const { slideshowAccessibilityProps } = useImageCarouselAccessibility();

  const handleCloseModal = useCallback(() => {
    setShowModal(false);
  }, []);

  const handleImageClick = useCallback(
    (index: number, url: string) => {
      if (isImageFailed(url)) {
        showToast({ text: t("imageCouldNotBeLoadedString") });
        return;
      }

      setActiveSlideImageIndex(index);
      setShowModal(true);
    },
    [isImageFailed, showToast, t]
  );

  const carousel = useMemo(() => {
    if (isLoading(urls)) {
      return <LoadingCarousel />;
    }
    return (
      <FixaOverflowCarousel id="images">
        {urls.map((url, index) => {
          const imageIsFailed = isImageFailed(url);
          const imageClassName = [
            styles["image"],
            imageIsFailed ? styles["image-fallback"] : "",
          ]
            .join(" ")
            .trim();
          return (
            <ClickableButtonWrapper
              key={url}
              className={styles["imageButtonClassName"]}
              onClick={() => handleImageClick(index, url)}
              aria-label={`Image ${index + 1} button`}
            >
              <FixaImage
                src={url}
                fallback={imageIsFailed}
                alt={`Image ${index + 1}`}
                className={imageClassName}
                onError={() => markImageAsFailed(url)}
              />
            </ClickableButtonWrapper>
          );
        })}
      </FixaOverflowCarousel>
    );
  }, [handleImageClick, isImageFailed, markImageAsFailed, urls]);

  const modal = useMemo(() => {
    if (isLoading(urls)) {
      return null;
    }
    return (
      <FixaModal visible={showModal} handleCloseBtn={handleCloseModal}>
        <FixaTheatre
          header={<FixaModalHeader />}
          ariaLabel={t("imageCarouselString")}
        >
          <FixaModalBody>
            <FixaCarousel
              {...slideshowAccessibilityProps}
              id={"images"}
              isSlideShow
              slideIndex={activeSlideImageIndex}
              className={styles["carousel"]}
            >
              {urls.map((url, index) => (
                <ZoomableImage key={url} src={url} alt={`Image ${index + 1}`} />
              ))}
            </FixaCarousel>
          </FixaModalBody>
        </FixaTheatre>
      </FixaModal>
    );
  }, [
    activeSlideImageIndex,
    handleCloseModal,
    slideshowAccessibilityProps,
    urls,
    showModal,
    t,
  ]);

  if (!isLoading(urls) && urls.length === 0) {
    return null;
  }

  return (
    <>
      <div>
        <FixaText textStyle="Heading.S">{t("imagesString")}</FixaText>
        {carousel}
      </div>
      {modal}
    </>
  );
};
